import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Undo2 } from "lucide-react";
import { Card, EmptyState, Loader } from "@/components/ui-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const norm = (s) => (s || "").split(/\s+/).filter(Boolean).join(" ").toLowerCase();
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "");
const COUNT_LABELS = { applications: "cases", invoices: "invoices", payments: "payments", client_files: "files", client_notes: "notes", appointments: "appointments", retainers: "retainers" };

export const restoreClient = async (token, onRestored) => {
  try { await api.post(`/deleted-items/${token}/restore`); toast.success("Client restored with all their records"); onRestored?.(); }
  catch (e) { toast.error(apiErr(e)); }
};

export function DeleteClientButton({ client, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = norm(typed) === norm(client.full_name);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/clients/${client.id}`, { data: { confirm_name: typed } });
      setOpen(false);
      toast.success(`${client.full_name} moved to Recently deleted (restorable for 30 days)`, {
        duration: 10000, action: { label: "Undo", onClick: () => restoreClient(data.undo_token) },
      });
      onDeleted?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => { setTyped(""); setOpen(true); }} data-testid="client-delete-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm border border-rose-200 text-rose-600 hover:bg-rose-50">
        <Trash2 className="h-4 w-4" /> Delete
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete {client.full_name}?</DialogTitle>
            <DialogDescription>All of this client's cases, invoices, payments, files, notes, appointments and their portal login will be removed. An admin can restore everything from Clients → Recently deleted for 30 days, then it's gone for good.</DialogDescription>
          </DialogHeader>
          <label className="text-xs font-medium text-slate-500">Type <b className="text-slate-800">{client.full_name}</b> to confirm</label>
          <input autoFocus data-testid="client-delete-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && ok && !busy) run(); }}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" placeholder={client.full_name} />
          <DialogFooter>
            <button data-testid="client-delete-cancel" onClick={() => setOpen(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold">Cancel</button>
            <button data-testid="client-delete-confirm" disabled={!ok || busy} onClick={run} className="px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold disabled:opacity-40">{busy ? "Deleting…" : "Delete client"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function BulkDeleteClientsButton({ ids, names, onDone }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const n = ids.length;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/clients/bulk-delete", { ids, confirm: typed.trim() });
      setOpen(false);
      toast.success(`${data.deleted} client${data.deleted === 1 ? "" : "s"} moved to Recently deleted (restorable for 30 days)`, {
        duration: 10000,
        action: { label: "Undo", onClick: async () => {
          let ok = 0;
          for (const t of data.undo_tokens) { try { await api.post(`/deleted-items/${t}/restore`); ok += 1; } catch { /* already restored */ } }
          ok ? toast.success(`${ok} client${ok === 1 ? "" : "s"} restored`) : toast.info("Those clients were already restored");
          onDone?.();
        } },
      });
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="client-bulk-delete" onClick={() => { setTyped(""); setOpen(true); }} className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 px-4 py-2 rounded-lg text-sm font-semibold text-white">
        <Trash2 className="h-4 w-4" /> Delete selected
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete {n} client{n === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription>Each client's cases, invoices, payments, files, notes, appointments and portal login are removed. Admins can restore them from Clients → Recently deleted for 30 days.</DialogDescription>
          </DialogHeader>
          <div className="max-h-32 overflow-y-auto text-sm text-slate-700 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2" data-testid="client-bulk-delete-names">{names.join(", ")}</div>
          <label className="text-xs font-medium text-slate-500">Type <b className="text-slate-800">DELETE</b> to confirm</label>
          <input autoFocus data-testid="client-bulk-delete-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE"
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
          <DialogFooter>
            <button data-testid="client-bulk-delete-cancel" onClick={() => setOpen(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold">Cancel</button>
            <button data-testid="client-bulk-delete-confirm" disabled={typed.trim() !== "DELETE" || busy} onClick={run} className="px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold disabled:opacity-40">{busy ? "Deleting…" : `Delete ${n} client${n === 1 ? "" : "s"}`}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function RecentlyDeletedClients({ onChanged }) {
  const [items, setItems] = useState(null);
  const load = () => api.get("/deleted-items?kind=client").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const purge = async (it) => {
    if (!window.confirm(`Permanently delete ${it.label} and all their records? This cannot be undone.`)) return;
    try { await api.delete(`/deleted-items/${it.id}`); toast.success("Deleted for good"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  if (!items) return <Loader />;
  if (!items.length) return <Card className="p-6"><EmptyState icon={Trash2} title="No recently deleted clients" description="Deleted clients stay here for 30 days with all their cases, files and payments, ready to restore." /></Card>;
  return (
    <Card className="divide-y divide-slate-100" data-testid="deleted-clients-list">
      {items.map((it) => (
        <div key={it.id} data-testid={`deleted-client-${it.id}`} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 truncate">{it.label}</div>
            <div className="text-xs text-slate-400">
              Deleted {when(it.deleted_at)}{it.deleted_by_name ? ` by ${it.deleted_by_name}` : ""} · removed for good {when(it.purge_after).split(",")[0]}
            </div>
            {it.counts && <div className="text-[11px] text-slate-500 mt-0.5">{Object.entries(COUNT_LABELS).filter(([k]) => it.counts[k]).map(([k, l]) => `${it.counts[k]} ${l}`).join(" · ")}</div>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid={`deleted-client-restore-${it.id}`} onClick={() => restoreClient(it.id, () => { load(); onChanged?.(); })} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100"><Undo2 className="h-3.5 w-3.5" /> Restore</button>
            <button data-testid={`deleted-client-purge-${it.id}`} onClick={() => purge(it)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-rose-600 text-xs font-semibold hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /> Delete forever</button>
          </div>
        </div>
      ))}
    </Card>
  );
}
